import { useEffect, useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { checkDate, WEEKDAYS } from "@/components/RecurringOptions";
import { CANCEL_REASONS } from "@/components/cancelReasons";

const KEEP = "__keep__";
const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
const toTime = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const today = () => format(new Date(), "yyyy-MM-dd");

// New slot of an item after the bulk move settings
const movedSlot = (r, mv) => {
  const d = parseISO(r.date);
  const shift = mv.day === KEEP ? 0 : ((Number(mv.day) + 6) % 7) - ((d.getDay() + 6) % 7);
  const start = mv.start || r.start_time;
  return {
    date: format(addDays(d, shift), "yyyy-MM-dd"), start_time: start,
    end_time: toTime(toMin(start) + toMin(r.end_time) - toMin(r.start_time)),
    trainer_id: mv.trainer === KEEP ? r.trainer_id : mv.trainer, room_id: mv.room === KEEP ? r.room_id : mv.room,
  };
};

const ItemList = ({ items, selected, toggle, extra }) => (
  <div className="max-h-56 overflow-y-auto space-y-1" data-testid="series-items">
    {items.map((r) => (
      <label key={r.reservation_id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg border ${selected.has(r.reservation_id) ? "bg-purple-50 border-purple-200" : "bg-white"}`} data-testid={`series-item-${r.reservation_id}`}>
        <Checkbox checked={selected.has(r.reservation_id)} onCheckedChange={() => toggle(r.reservation_id)} />
        <span className="font-semibold w-28">{format(parseISO(r.date), "d MMM EEE", { locale: tr })}</span>
        <span>{r.start_time}–{r.end_time}</span>
        <span className="text-grey truncate">{r.trainer_name}{r.open_class_id ? " · Online" : ""}</span>
        {extra?.(r)}
      </label>
    ))}
  </div>
);

const MoveFields = ({ mv, setMv, trainers, rooms }) => (
  <div className="grid grid-cols-2 gap-2">
    <div>
      <Label className="text-xs">Gün</Label>
      <Select value={mv.day} onValueChange={(v) => setMv({ ...mv, day: v })}>
        <SelectTrigger className="h-9 rounded-lg mt-1" data-testid="series-move-day"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={KEEP}>Aynı gün</SelectItem>
          {WEEKDAYS.map((w) => <SelectItem key={w.d} value={String(w.d)}>{w.l} (aynı hafta)</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-xs">Yeni başlangıç saati</Label>
      <Input type="time" value={mv.start} onChange={(e) => setMv({ ...mv, start: e.target.value })} className="h-9 rounded-lg mt-1" data-testid="series-move-start" />
    </div>
    <div>
      <Label className="text-xs">Antrenör</Label>
      <Select value={mv.trainer} onValueChange={(v) => setMv({ ...mv, trainer: v })}>
        <SelectTrigger className="h-9 rounded-lg mt-1" data-testid="series-move-trainer"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={KEEP}>Aynı antrenör</SelectItem>
          {trainers.map((t) => <SelectItem key={t.trainer_id} value={t.trainer_id}>{t.name} {t.surname}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-xs">Oda</Label>
      <Select value={mv.room} onValueChange={(v) => setMv({ ...mv, room: v })}>
        <SelectTrigger className="h-9 rounded-lg mt-1" data-testid="series-move-room"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={KEEP}>Aynı oda</SelectItem>
          {rooms.map((r) => <SelectItem key={r.room_id} value={r.room_id}>{r.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  </div>
);

const CancelFields = ({ cat, setCat, text, setText }) => (
  <div className="space-y-2">
    <Label className="text-xs">İptal nedeni</Label>
    <Select value={cat} onValueChange={setCat}>
      <SelectTrigger className="h-9 rounded-lg" data-testid="series-cancel-reason"><SelectValue placeholder="Neden seçin" /></SelectTrigger>
      <SelectContent>{CANCEL_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
    </Select>
    {cat === "other" && <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Açıklama" className="h-9 rounded-lg" data-testid="series-cancel-text" />}
    <p className="text-[11px] text-grey">Dersler hak yakılmadan normal iptal edilir; paket hakkı iade edilir. Online derste başka kişi yoksa online ders de silinir.</p>
  </div>
);

const updateBody = (r, slot) => ({
  member_id: r.member_id, member_package_id: r.payment_type === "package" ? r.member_package_id : null, branch: r.branch,
  training_type: r.training_type, allow_shared: r.allow_shared !== false, status: "approved", payment_type: r.payment_type,
  is_demo: r.payment_type === "demo", card_type: r.payment_type === "card" ? (r.card_type || r.card_name) : null,
  add_to_online: !!r.open_class_id, ...slot,
});

export const SeriesManageDialog = ({ reservation, businessId, trainers, rooms, onClose, onDone }) => {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState("move");
  const [mv, setMv] = useState({ day: KEEP, start: "", trainer: KEEP, room: KEEP });
  const [cat, setCat] = useState("");
  const [text, setText] = useState("");
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/reservations/${reservation.reservation_id}/series`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => { setItems(d.items || []); setSelected(new Set((d.items || []).filter((x) => x.in_series).map((x) => x.reservation_id))); })
      .catch(() => setItems([]));
  }, [reservation.reservation_id]);

  const chosen = (items || []).filter((r) => selected.has(r.reservation_id));
  const moveKey = JSON.stringify({ mv, ids: [...selected] });
  const moveActive = mv.day !== KEEP || !!mv.start || mv.trainer !== KEEP || mv.room !== KEEP;
  useEffect(() => {
    if (mode !== "move" || !moveActive) { setChecks({}); return undefined; }
    let alive = true;
    setChecks({});
    const timer = setTimeout(async () => {
      const rows = await Promise.all(chosen.map(async (r) => {
        const s = movedSlot(r, mv);
        if (s.date < today()) return [r.reservation_id, ["Geçmiş tarih"]];
        return [r.reservation_id, await checkDate({ date: s.date, startTime: s.start_time, endTime: s.end_time, trainerId: s.trainer_id, roomId: s.room_id, branch: r.branch, trainingType: r.training_type, excludeId: r.reservation_id, memberId: r.member_id, businessId })];
      }));
      if (alive) setChecks(Object.fromEntries(rows));
    }, 500);
    return () => { alive = false; clearTimeout(timer); };
  }, [moveKey, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const okMoves = chosen.filter((r) => checks[r.reservation_id] && !checks[r.reservation_id].length);
  const checking = mode === "move" && moveActive && chosen.some((r) => checks[r.reservation_id] === undefined);

  const run = async () => {
    setBusy(true);
    let ok = 0;
    const failed = [];
    const list = mode === "move" ? okMoves : chosen;
    for (const r of list) {
      const url = mode === "move" ? `${API}/reservations/${r.reservation_id}` : `${API}/reservations/${r.reservation_id}/cancel`;
      const body = mode === "move" ? updateBody(r, movedSlot(r, mv)) : {
        reason: cat === "other" ? text.trim() : CANCEL_REASONS.find((x) => x.value === cat)?.label, reason_category: cat,
        burn_session: false, force: true, delete_online: true,
      };
      const res = await fetch(url, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) });
      if (res.ok) ok++;
      else failed.push(`${format(parseISO(r.date), "d MMM", { locale: tr })}: ${(await res.json().catch(() => ({}))).detail || "hata"}`);
    }
    setBusy(false);
    if (ok) toast.success(mode === "move" ? `${ok} rezervasyon taşındı` : `${ok} rezervasyon iptal edildi`);
    if (failed.length) toast.error(`${failed.length} rezervasyon işlenemedi — ${failed.slice(0, 3).join(" · ")}`, { duration: 8000 });
    if (ok) onDone();
  };

  const canRun = !busy && (mode === "move" ? moveActive && !checking && okMoves.length > 0 : chosen.length > 0 && cat && (cat !== "other" || text.trim()));
  const extra = (r) => {
    if (mode !== "move" || !moveActive || !selected.has(r.reservation_id)) return null;
    const s = movedSlot(r, mv);
    const c = checks[r.reservation_id];
    return (
      <span className={`ml-auto shrink-0 font-medium ${c?.length ? "text-red-600" : "text-emerald-700"}`}>
        → {format(parseISO(s.date), "d MMM EEE", { locale: tr })} {s.start_time}{c === undefined ? " · kontrol" : c.length ? ` · ${c[0]}` : ""}
      </span>
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl md:max-h-[90vh] overflow-y-auto content-start" data-testid="series-manage-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-navy"><Layers className="w-5 h-5" /> Seriyi Yönet · {reservation.member_name}</DialogTitle></DialogHeader>
        {items === null ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : items.length === 0 ? (
          <p className="text-sm text-grey" data-testid="series-empty">Üyenin ileri tarihli aktif rezervasyonu yok.</p>
        ) : (
          <>
            <p className="text-xs text-grey">Üyenin ileri tarihli aktif dersleri. Aynı serideki dersler işaretli geldi; istediğinizi ekleyip çıkarabilirsiniz. Seçili: <b>{chosen.length}</b></p>
            <ItemList items={items} selected={selected} toggle={toggle} extra={extra} />
            <div className="flex gap-2">
              <Button size="sm" variant={mode === "move" ? "default" : "outline"} className="rounded-lg" onClick={() => setMode("move")} data-testid="series-mode-move">Toplu Taşı</Button>
              <Button size="sm" variant={mode === "cancel" ? "default" : "outline"} className={`rounded-lg ${mode === "cancel" ? "bg-red-600 hover:bg-red-700" : "text-red-600"}`} onClick={() => setMode("cancel")} data-testid="series-mode-cancel">Toplu İptal</Button>
            </div>
            {mode === "move" ? <MoveFields mv={mv} setMv={setMv} trainers={trainers} rooms={rooms} /> : <CancelFields cat={cat} setCat={setCat} text={text} setText={setText} />}
            {mode === "move" && moveActive && <p className="text-[11px] text-grey">Taşıma direkt onaylı yapılır, üyeye onaya gitmez. Uygun olmayan (kırmızı) dersler taşınmaz.</p>}
          </>
        )}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={onClose} data-testid="series-close-btn">Vazgeç</Button>
          <Button className={`flex-1 rounded-xl ${mode === "cancel" ? "bg-red-600 hover:bg-red-700" : "bg-navy hover:bg-navy/90"}`} disabled={!canRun} onClick={run} data-testid="series-run-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "move" ? `${okMoves.length} rezervasyonu taşı` : `${chosen.length} rezervasyonu iptal et`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
